import { LoaderFunction, useRouteLoaderData } from "react-router-dom";
import { DeweySchema } from "./helpers/brandSchema.ts";
import chameleonjs from "chameleonjs";
import { isLocalDev } from "../../common/env.ts";
import deepmerge from "deepmerge";
import { loadLocale, MessageInfo } from "../../locale/loadLocale.ts";
import { isVstLocale } from "../../common/utils/locale.ts";
import { getCookie } from "../../common/utils/cookie.ts";
import { once } from "../../common/utils/function.ts";

export const rootRouteId = "root";

export const featureCookieName = "dewey-features";

export enum FeatureName {
  // Typescript gets angry if the enum is empty
  TempPlaceholder = "temp-placeholder",
  ShortNewFeatures = "short-new-features",
}

export function shouldRevalidateRoot() {
  return false;
}

function addAdditionalBrandDefaults(brand: DeweySchema): DeweySchema {
  const brandClone = deepmerge(brand, {});
  // This is configured in chameleon as an override and is sometimes an empty string
  brandClone.DownloadOffline.appName =
    brand.DownloadOffline.appName || "Bookshelf";
  // Chameleon is not hopping back to the base logo if there is no custom aloha logo. Quick fix for release.
  brandClone.logo =
    brand.logo ||
    "https://storage.googleapis.com/vst-chameleon-prod/multipart-1655496719-276917903038439-1";
  // Some people input store URLs without the protocol
  if (
    brand.Store.enabled &&
    brand.Store.vstStore &&
    !brand.Store.url.startsWith("http")
  ) {
    brandClone.Store.url = "https://" + brand.Store.url;
  }

  return brandClone;
}

export const loadBrandOnce = once(() => {
  const envBrand = import.meta.env.VITE_BRAND_HOST as string | undefined;

  const promises: Promise<Partial<DeweySchema>>[] = [
    chameleonjs.loadBrand<DeweySchema>(
      envBrand ? envBrand : window.location.hostname,
      "dewey"
    ),
  ];

  if (isLocalDev()) {
    promises.push(
      fetch("/local-brand.json")
        .then((response) => response.json() as Partial<DeweySchema>)
        .catch(() => ({}))
    );
  } else {
    promises.push(Promise.resolve({}));
  }

  return Promise.all(promises).then(([chameleonData, localData]) => {
    return addAdditionalBrandDefaults(
      deepmerge<DeweySchema>(chameleonData, localData)
    );
  });
});

interface RootLoaderData {
  brand: DeweySchema;
  messageInfo: MessageInfo | undefined;
}

export const rootLoader: LoaderFunction = async () => {
  const brand = await loadBrandOnce();
  let messageInfo = undefined;
  if (isVstLocale(brand.locale)) {
    messageInfo = await loadLocale(brand.locale);
  }
  return {
    brand,
    messageInfo,
  };
};

function useRootLoaderData() {
  return useRouteLoaderData(rootRouteId) as RootLoaderData;
}

export function useBrandLoaderData() {
  return useRootLoaderData().brand;
}

export function useRootLoaderMessageInfo() {
  return useRootLoaderData().messageInfo;
}

export function useRootLoaderFeatures(): Partial<Record<FeatureName, boolean>> {
  const cookieVal = getCookie(featureCookieName);
  return cookieVal
    ? (JSON.parse(cookieVal) as Partial<Record<string, boolean>>)
    : {};
}

export function useRootLoaderFeatureEnabled(name: FeatureName): boolean {
  const data = useRootLoaderFeatures();
  return data[name] ?? false;
}
